<template>
  <div class="resource-list">
    <a-spin :spinning="loading">
      <div v-if="files.length === 0 && !loading" class="empty-state">
        <i class="fa fa-folder-open empty-icon"></i>
        <p>파일이 없습니다.</p>
        <p class="empty-hint">업로드 버튼을 클릭하여 파일을 추가하세요.</p>
      </div>

      <div v-else class="file-items">
        <div
          v-for="item in files"
          :key="item.path"
          class="file-item"
          :class="{ 'is-folder': item.isFolder, 'is-clickable': item.isFolder }"
          @click="item.isFolder ? $emit('folder-click', item) : null"
        >
          <div class="file-info">
            <div class="file-icon" :class="{ 'folder-icon': item.isFolder, 'image-icon': isImageFile(item) }">
              <!-- 이미지 파일인 경우 썸네일 표시 -->
              <img v-if="isImageFile(item) && item.url" :src="item.url" :alt="item.name" class="image-thumbnail" />
              <!-- 아이콘 표시 -->
              <i v-else :class="getItemIcon(item)"></i>
            </div>
            <div class="file-details">
              <div class="file-name">
                {{ item.name }}
                <span v-if="item.isFolder" class="folder-badge">폴더</span>
              </div>
              <div class="file-meta" v-if="!item.isFolder">
                <span class="file-size">{{ formatFileSize(item.size) }}</span>
                <span class="file-separator">•</span>
                <span class="file-date">{{ formatDate(item.lastModified) }}</span>
              </div>
              <div class="file-meta" v-else>
                <span class="folder-hint">클릭하여 열기</span>
              </div>
            </div>
          </div>

          <div class="file-actions" v-if="!item.isFolder">
            <a-button
              size="small"
              @click.stop="$emit('preview', item)"
              title="미리보기"
            >
              <template #icon><i class="fa fa-eye"></i></template>
            </a-button>
            <a-button
              size="small"
              @click.stop="$emit('copy-url', item)"
              title="URL 복사"
            >
              <template #icon><i class="fa fa-copy"></i></template>
            </a-button>
          </div>
        </div>
      </div>
    </a-spin>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'ResourceList',

  props: {
    files: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  emits: ['preview', 'copy-url', 'folder-click'],

  setup() {
    // 이미지 파일 판단
    const isImageFile = (item) => {
      if (item.isFolder || !item.name) return false;
      const ext = item.name.split('.').pop()?.toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
    };

    // 항목 아이콘 반환 (폴더 또는 파일)
    const getItemIcon = (item) => {
      // 폴더인 경우
      if (item.isFolder) {
        return 'fa fa-folder';
      }

      // 파일인 경우
      if (!item.name) return 'fa fa-file';

      const ext = item.name.split('.').pop()?.toLowerCase();

      // 이미지
      if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
        return 'fa fa-file-image';
      }

      // PDF
      if (ext === 'pdf') {
        return 'fa fa-file-pdf';
      }

      // 워드
      if (['doc', 'docx'].includes(ext)) {
        return 'fa fa-file-word';
      }

      // 엑셀
      if (['xls', 'xlsx'].includes(ext)) {
        return 'fa fa-file-excel';
      }

      // 텍스트
      if (['txt', 'json'].includes(ext)) {
        return 'fa fa-file-alt';
      }

      return 'fa fa-file';
    };

    // 파일 크기 포맷
    const formatFileSize = (bytes) => {
      if (!bytes || bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    };

    // 날짜 포맷
    const formatDate = (dateString) => {
      if (!dateString) return '-';

      try {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${year}-${month}-${day} ${hours}:${minutes}`;
      } catch (error) {
        return dateString;
      }
    };

    return {
      isImageFile,
      getItemIcon,
      formatFileSize,
      formatDate
    };
  }
});
</script>

<style lang="scss" scoped>
.resource-list {
  min-height: 300px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;

  .empty-icon {
    font-size: 48px;
    color: #d9d9d9;
    margin-bottom: 16px;
  }

  p {
    margin: 0 0 8px 0;
    font-size: 14px;
    color: #999;
  }

  .empty-hint {
    font-size: 12px;
    color: #bbb;
  }
}

.file-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background-color: #f9f9f9;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f0f0f0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  &.is-clickable {
    cursor: pointer;

    &:hover {
      background-color: #e6f4ff;
      border: 1px solid #91caff;
    }
  }

  &.is-folder {
    background-color: #fafafa;
  }
}

.file-info {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 12px;
}

.file-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  border-radius: 6px;
  font-size: 18px;
  color: #666;
  overflow: hidden;

  &.folder-icon {
    color: #faad14;
    font-size: 20px;
  }

  &.image-icon {
    padding: 0;
    background-color: #f0f0f0;
  }

  .image-thumbnail {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 6px;
  }
}

.file-details {
  flex: 1;
  min-width: 0;
}

.file-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  .folder-badge {
    display: inline-block;
    margin-left: 8px;
    padding: 2px 8px;
    background-color: #e6f4ff;
    color: #1677ff;
    font-size: 11px;
    border-radius: 4px;
    font-weight: 600;
  }
}

.file-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #999;

  .folder-hint {
    color: #1677ff;
    font-style: italic;
  }
}

.file-size,
.file-date {
  white-space: nowrap;
}

.file-separator {
  color: #ddd;
}

.file-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

// 반응형
@media (max-width: 768px) {
  .file-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .file-info {
    width: 100%;
  }

  .file-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
